import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Top-level error boundary (V4.17.2).
 *
 * Catches uncaught render / lifecycle errors anywhere below it. The app
 * previously had NO error boundary — any unhandled error rendered the
 * entire page blank (the "white screen of death" reported when admin
 * logged out and re-logged in as an agent on the same browser session,
 * triggering a stale-context exception).
 *
 * Behaviour:
 *  - Shows a friendly fallback UI with the error message + a button to
 *    reload the app and clear the corrupted in-memory state.
 *  - Logs the error to the console for support / debug.
 *  - Surfaces a "Sign in again" CTA that nukes localStorage too, since
 *    the most common cause is a stale token / role mismatch.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught render error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleSignInAgain = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      // Don't blow away `selectedEntity` etc. — only the auth bits.
    } catch (e) { /* ignore */ }
    window.location.href = "/login";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || "An unexpected error occurred.";
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
            <AlertTriangle className="h-7 w-7 text-amber-500" strokeWidth={1.5} />
          </div>
          <h1 className="text-lg font-semibold text-gray-900 mb-1">Something went wrong</h1>
          <p className="text-sm text-gray-500 leading-relaxed mb-5">
            The page hit an unexpected error. Try reloading — if it keeps happening, sign in again.
          </p>
          <details className="text-left bg-gray-50 rounded-lg p-3 mb-5 text-xs text-gray-600 font-mono break-words">
            <summary className="cursor-pointer text-gray-500 font-sans mb-1">Show technical details</summary>
            <div className="mt-2 whitespace-pre-wrap">{msg}</div>
          </details>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={this.handleReload}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-br from-[#4E50FF] to-[#6366F1] text-white text-sm font-medium shadow hover:opacity-95"
              data-testid="error-boundary-reload"
            >
              <RefreshCw className="h-4 w-4" /> Reload page
            </button>
            <button
              onClick={this.handleSignInAgain}
              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50"
              data-testid="error-boundary-signin"
            >
              Sign in again
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
